import type { BlueprintVariant, BlueprintVersion } from 'state';

/**
 * THE ONE THING TO DO NEXT, per variant (the admin's journey, step 1).
 *
 * The grid already knows everything that decides what an admin has to do
 * with a variant -- whether the version was launched (frozen), whether the
 * launch check passes, and why not -- but it used to leave the inference to
 * the reader. This turns that state into one sentence and, where there is
 * one, one button. It never invents a state: with no launch summary at all it
 * says nothing.
 *
 * A launched version is frozen because trainee results describe it, so its
 * remedy is never "edit it": it is a new version, and that button belongs on
 * the row that needs it, not in the page header.
 */
export type NextStepKind =
  | 'ready' // in service: the launch check passes
  | 'publish' // in service, but the version is still a draft
  | 'derive' // no answer key yet
  | 'regenerate' // the key was built from inputs that no longer match
  | 'updating' // the automatic update is queued, running or waiting on a review
  | 'attention' // refused for another named reason
  | 'frozen' // launched: cannot change; a new version is the route
  | 'unknown'; // no launch summary was delivered

export type NextStep = {
  kind: NextStepKind;
  /** The one button the cell offers, or null when there is nothing to press here. */
  action: string | null;
  /** One sentence saying why, in the admin's words. */
  hint: string;
  /** The version column offers "New version from vN" as THE step. */
  wantsNewVersion: boolean;
};

const firstSentence = (text?: string | null): string => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^.*?[.!?](?=\s|$)/);
  return match ? match[0] : trimmed;
};

export const nextStepFor = (
  version: Pick<BlueprintVersion, 'version_number' | 'is_locked'>,
  variant: Pick<BlueprintVariant, 'launch_summary'>,
  latestVersionNumber: number,
  published: boolean,
): NextStep => {
  const summary = variant.launch_summary;
  const n = version.version_number;
  const isLatest = n === latestVersionNumber;
  if (!summary || summary.launchable == null) {
    return { kind: 'unknown', action: null, hint: '', wantsNewVersion: false };
  }
  if (summary.launchable) {
    return published
      ? {
          kind: 'ready',
          action: null,
          hint: 'The answer key is in service; trainers can launch this variant.',
          wantsNewVersion: false,
        }
      : {
          kind: 'publish',
          action: null,
          hint: 'The answer key is in service. Publish the version to offer it to trainers.',
          wantsNewVersion: false,
        };
  }
  if (version.is_locked) {
    return {
      kind: 'frozen',
      action: null,
      hint: isLatest
        ? `v${n} was launched, so its exercise is frozen. Changes and a new answer key go into v${n + 1}.`
        : `v${n} was launched, so its exercise is frozen. The current version is v${latestVersionNumber}.`,
      wantsNewVersion: isLatest,
    };
  }
  const status = String(summary.answer_key_status || '').toLowerCase();
  if (!status || status === 'no_key' || status === 'none') {
    return {
      kind: 'derive',
      action: 'Derive the answer key',
      hint: 'No answer key has been derived for this version yet.',
      wantsNewVersion: false,
    };
  }
  if (status === 'identity_mismatch') {
    return {
      kind: 'regenerate',
      action: 'Regenerate the answer key',
      hint: 'The answer key was built from inputs that no longer match this version.',
      wantsNewVersion: false,
    };
  }
  if (status === 'assessment_updating') {
    return {
      kind: 'updating',
      action: 'Check the answer key',
      hint:
        firstSentence(summary.launch_refusal) ||
        'The answer key is being updated.',
      wantsNewVersion: false,
    };
  }
  return {
    kind: 'attention',
    action: 'Review the answer key',
    hint:
      firstSentence(summary.launch_refusal) ||
      'The launch check refuses this variant.',
    wantsNewVersion: false,
  };
};
